import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from './api.ts';
import { setLocale } from './i18n.ts';

export interface Me {
  id: string;
  email: string;
  name: string;
  role: 'coach' | 'student';
  locale: string;
  coachProfile?: {
    onboardingDone: boolean;
    isPublicProfile: boolean;
    referralCode: string;
    bio: string;
    specialties: string;
    languages: string;
    introVideoUrl: string | null;
    brandName: string | null;
    vatId: string | null;
  } | null;
  studentProfile?: {
    coachId: string;
    voiceType: string | null;
    rangeLowMidi: number | null;
    rangeHighMidi: number | null;
    goals: string;
    recapShareEnabled: boolean;
  } | null;
  studioMembers?: { role: string; studioId: string; studio: { id: string; name: string; brandColor: string } }[];
}

interface AuthCtx {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (token: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx>({
  me: null,
  loading: true,
  refresh: async () => {},
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const user = await api<Me>('/api/auth/me');
      setMe(user);
      if (user.locale) setLocale(user.locale);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (token: string) => {
      setToken(token);
      setLoading(true);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(() => {
    setToken(null);
    setMe(null);
  }, []);

  return <Ctx.Provider value={{ me, loading, refresh, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
